import { useState, useMemo, useEffect, useRef } from 'react';
import { Box, CardMedia, CircularProgress, Stack } from '@mui/material';
import NoSearchConditionImg from '/assets/image/no_search_condition.svg';
import noFileImg from '/assets/image/no_file.svg';
import { internet, resource } from './Resource.style';
import { useSelector } from 'react-redux';
import { StateCard, InternatCard, BookCard } from '~/components/Global/Card';
import { FilterTab } from '~/components/Global/FilterTab';
import { SearchCategory, SIDEBAR, INTERNET_FILTER_TAB } from '~/types/enum';
import {
  resourceInternetLinkClickEvent,
  resourceInternetTabEvent,
} from '~/tracking';
import { chain, mapValues, uniqBy } from 'lodash';
import {
  getterClientResourceFilter,
  getterClientResources,
  getterClientResourcesPayload,
  getterMultiLevelClientResources,
  getterProgressLookup,
} from '~/store/resource/resourceGetter';
import { withLiveProgress } from '~/utils/helper/downloadFile';
import {
  getterInternetResourcesPayload,
  getterInternetResources,
} from '~/store/internetResource/internetResourceGetter';
import { ListDownloadGroup } from '~/components/Resource/ListDownloadGroup';
import { Pagination } from '~/components/Global/Pagination';
import { setSearchCondition } from '~/store/resource';
import { setSearchCondition as setInterSearchCondition } from '~/store/internetResource';
import { RootState, useAppDispatch } from '~/store';
import { TDownloadableResource } from '~/types/common.type';
import {
  internetCategory,
  resourceCategory,
  resourceEduCode,
  resourceVersion,
  TInternetResource,
} from '~/api/oneBoxV3/oneBoxV3.type';
import {
  oneBoxV3Api,
  useInternetDropDownListQuery,
  useLazyGetInternetResourcesQuery,
} from '~/api/oneBoxV3';
import { ToTopButton } from '~/components/ToTopButton/ToTopButton';
import { IssueButton } from '~/components/IssueButton/IssueButton';
import { useInfiniteScroll } from 'react-infinite-scroll-hook';
import { bookCard } from '~/components/Global/Card/Card.style';
import tikuCloud from '/assets/image/tiku/tikuCloud.png';
import {
  BaseButton,
  BASE_BUTTON_COLOR,
  BASE_BUTTON_VARIANT,
} from '~/components/Global/Button';
import { useCommon } from '~/utils/hook/useCommon';
import { SiteFooter } from '~/components/Global/SiteFooter';
import { resolveInternetPageSize } from '~/utils/constants';
import { FileStatus } from '~/store/resource/resource.type';
import { ResourceLatestNews } from '~/components/LatestNews/ResourceLatestNews';
import { isMac, isWeb } from '~/utils/helper/device';

const EBOOK_PLATFORM = {
  WINDOWS: 'Windows 電腦版',
  MACOS: 'macOS 電腦版',
  WEB: '網頁版',
} as const;

type EbookPlatform = (typeof EBOOK_PLATFORM)[keyof typeof EBOOK_PLATFORM];

const EBOOK_PLATFORM_TABS_BY_EDU_CODE: Record<string, EbookPlatform[]> = {
  [resourceEduCode.E]: [
    EBOOK_PLATFORM.WINDOWS,
    EBOOK_PLATFORM.WEB,
    EBOOK_PLATFORM.MACOS,
  ],
  [resourceEduCode.J]: [EBOOK_PLATFORM.WINDOWS, EBOOK_PLATFORM.WEB],
};

// store 的 eduCodes 為 string，與 enum 成員直接比較會觸發 no-unsafe-enum-comparison
const WEB_DEFAULT_EDU_CODES: string[] = [resourceEduCode.E, resourceEduCode.J];

const EBOOK_WINDOWS_EXTENSIONS = ['iso'];
const EBOOK_MACOS_EXTENSIONS = ['pkg', 'dmg'];
const EBOOK_EXTENSIONS = [
  ...EBOOK_WINDOWS_EXTENSIONS,
  ...EBOOK_MACOS_EXTENSIONS,
];

const stripEbookFileExtension = (name: string): string =>
  name.replace(new RegExp(`\\.(${EBOOK_EXTENSIONS.join('|')})$`, 'i'), '');

const getCurrentEbookPlatform = (
  tabs: EbookPlatform[],
  eduCode: string,
): EbookPlatform => {
  // 目前桌機版僅有 Windows Electron；Mac Electron 尚未納入需求範圍。
  if (!isWeb) return EBOOK_PLATFORM.WINDOWS;
  // 國小、國中以瀏覽器開啟時預設「網頁版」，不依作業系統選桌機版頁籤。
  if (
    WEB_DEFAULT_EDU_CODES.includes(eduCode) &&
    tabs.includes(EBOOK_PLATFORM.WEB)
  ) {
    return EBOOK_PLATFORM.WEB;
  }
  if (isMac && tabs.includes(EBOOK_PLATFORM.MACOS)) {
    return EBOOK_PLATFORM.MACOS;
  }
  return EBOOK_PLATFORM.WINDOWS;
};

const getResourceFileExtension = ({
  resource: item,
}: TDownloadableResource): string => {
  const source = item.fileName || item.file.fileName || item.file.url;
  const cleanSource = source.split(/[?#]/)[0];
  return cleanSource.match(/\.([a-z0-9]+)$/i)?.[1]?.toLowerCase() ?? '';
};

function orderByINTERNET_FILTER_TAB(
  col: string[],
  internetFilterTabEnum: typeof INTERNET_FILTER_TAB,
): string[] {
  // 標準五類永遠保留順序；其餘（含 GGB、AI工具等）僅在當前篩選下 API 有回傳的 resources2 才顯示
  const ordered: string[] = [...Object.values(internetFilterTabEnum)];
  for (const item of col) {
    if (!ordered.includes(item)) {
      ordered.push(item);
    }
  }
  return ordered;
}

function convertInternetCategoryToResourceCategory(
  category: internetCategory,
): resourceCategory {
  // 將 internetCategory 轉換為 resourceCategory
  switch (category) {
    case internetCategory.SCHOOL:
      return resourceCategory.SCHOOL;
    case internetCategory.PUBLIC:
      return resourceCategory.SCHOOL;
  }
}

export const emptyProgress = {
  key: undefined,
  status: FileStatus.non,
  fileFormat: '',
  filePath: '',
  progressRatio: 0,
  speedBytes: 0,
  speed: '-',
  totalBytes: 0,
  total: '-',
  downloaded: '-',
  downloadedBytes: 0,
  updateTime: 0,
  installingProgressRatio: 0,
  downloadedAt: 0,
};

export const Resource = () => {
  const { openExternal } = useCommon();
  // payload 內的 page 已轉為 API 用的 1-indexed，元件內的累積／重置邏輯依 store 的 0-indexed page
  const storePage = useSelector(
    (state: RootState) => state.resource.searchCondition.page,
  );
  const { eduCodes, years, grades, subjects, resources1 } = useSelector(
    getterClientResourceFilter,
  );
  const resourcePayload = useSelector(getterClientResourcesPayload);
  const internetResourcePayload = useSelector(getterInternetResourcesPayload);

  // 取得 API 讀取狀態
  const { status: resourcesStatus } = useSelector((state: RootState) =>
    oneBoxV3Api.endpoints.getResources.select(resourcePayload)(state),
  ) || { status: 'uninitialized' };
  const isResourcesFetching = String(resourcesStatus) === 'pending';

  const { status: internetResourcesStatus } = useSelector((state: RootState) =>
    oneBoxV3Api.endpoints.getInternetResources.select(internetResourcePayload)(
      state,
    ),
  ) || { status: 'uninitialized' };
  const isInternetResourcesFetching =
    String(internetResourcesStatus) === 'pending';

  /** 未選年級時後端單次查詢常漏 GGB 等依年級標記的筆數，改依年級清單並行查詢後合併 */
  const shouldMergeInternetResourcesByAllGrades =
    resources1 === SIDEBAR.internet &&
    (!grades || grades === '' || grades === 'all');

  const {
    data: internetGradesDropdown,
    isFetching: isFetchingInternetGradesList,
  } = useInternetDropDownListQuery(
    {
      isLaunch: true,
      categories: [SearchCategory.SCHOOL, SearchCategory.PUBLIC],
      internetResourceProps: ['grades'],
      search: {
        resources1: resources1 || SIDEBAR.internet,
        eduCodes: eduCodes!,
        subjects: subjects!,
        years: years ?? undefined,
      },
    },
    {
      skip: !shouldMergeInternetResourcesByAllGrades || !eduCodes || !subjects,
    },
  );

  const gradeOptionsForInternetMerge =
    internetGradesDropdown?.data?.uniqInternetResourceProps?.grades ?? [];

  const [mergedInternetResources, setMergedInternetResources] = useState<
    TInternetResource[] | null
  >(null);
  const [isMergingInternetByGrades, setIsMergingInternetByGrades] =
    useState(false);

  const [triggerInternetResourcesByGrade] = useLazyGetInternetResourcesQuery();

  // 學年度／科目等變更時先清合併結果，避免短暫顯示上一組條件的資料
  useEffect(() => {
    if (!shouldMergeInternetResourcesByAllGrades) return;
    setMergedInternetResources(null);
  }, [
    shouldMergeInternetResourcesByAllGrades,
    internetResourcePayload.search?.years,
    internetResourcePayload.search?.eduCodes,
    internetResourcePayload.search?.subjects,
  ]);

  useEffect(() => {
    if (!shouldMergeInternetResourcesByAllGrades) {
      setMergedInternetResources(null);
      setIsMergingInternetByGrades(false);
      return;
    }
    // 年級清單仍載入中：不視為「可合併」也不清空（避免與 RTK 快取競態造成閃爍）
    if (isFetchingInternetGradesList) {
      return;
    }
    if (gradeOptionsForInternetMerge.length === 0) {
      setMergedInternetResources(null);
      setIsMergingInternetByGrades(false);
      return;
    }
    let cancelled = false;
    setIsMergingInternetByGrades(true);
    void (async () => {
      try {
        const results = await Promise.all(
          gradeOptionsForInternetMerge.map(grade =>
            triggerInternetResourcesByGrade({
              ...internetResourcePayload,
              search: {
                ...internetResourcePayload.search,
                grades: grade,
              },
            }).unwrap(),
          ),
        );
        if (cancelled) return;
        const flat = results.flatMap(r => r.data?.data ?? []);
        setMergedInternetResources(uniqBy(flat, '_id'));
      } catch {
        if (!cancelled) setMergedInternetResources([]);
      } finally {
        if (!cancelled) setIsMergingInternetByGrades(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    shouldMergeInternetResourcesByAllGrades,
    gradeOptionsForInternetMerge.join('\u0000'),
    isFetchingInternetGradesList,
    internetResourcePayload.search?.years,
    internetResourcePayload.search?.eduCodes,
    internetResourcePayload.search?.subjects,
    internetResourcePayload.pagination?.page,
    internetResourcePayload.pagination?.pageSize,
    triggerInternetResourcesByGrade,
  ]);

  const internetResourcesFromStore = useSelector(state =>
    getterInternetResources(state, internetResourcePayload),
  );

  const internetResources = useMemo(() => {
    // 僅在「未選年級、走合併查詢」時採用 merged；否則若只看 merged !== null，
    // 選完年級後 effect 尚未清 state 前會短暫顯示錯誤的合併結果
    if (
      shouldMergeInternetResourcesByAllGrades &&
      mergedInternetResources !== null
    ) {
      return {
        resources: mergedInternetResources,
        totalResourcesCount: mergedInternetResources.length,
      };
    }
    if (shouldMergeInternetResourcesByAllGrades) {
      // Layout 已 skip 預設查詢，store 仍可能短暫命中 RTK 舊快取 → 誤顯示後又變空造成閃爍
      return {
        resources: [],
        totalResourcesCount: 0,
      };
    }
    return internetResourcesFromStore;
  }, [
    shouldMergeInternetResourcesByAllGrades,
    mergedInternetResources,
    internetResourcesFromStore,
  ]);

  const isInternetResourcesLoading =
    isInternetResourcesFetching ||
    isMergingInternetByGrades ||
    (shouldMergeInternetResourcesByAllGrades && isFetchingInternetGradesList);
  const getResource2 = useMemo(() => {
    return chain(internetResources.resources)
      .flatMap(obj => obj.resources2)
      .filter(res2 => res2 !== '網頁PPT')
      .uniq()
      .value();
  }, [internetResources]);

  const sortedTabs = useMemo(() => {
    return orderByINTERNET_FILTER_TAB(getResource2, INTERNET_FILTER_TAB);
  }, [getResource2]);

  const internetResourcesGrouped = useMemo(() => {
    const result: {
      [resource1: string]: {
        [resource2: string]: {
          [resource3: string]: TDownloadableResource[];
        };
      };
    } = {};

    internetResources.resources
      .filter(item => item.resources2.includes('網頁PPT'))
      .forEach(item => {
        item.resources1.forEach(res1 => {
          const resources2 =
            item.resources2.length > 0 ? item.resources2 : [res1];
          const resources3 = item.urlName || '';

          resources2.forEach(res2 => {
            // 只處理 resources2 為"網頁PPT"的情況
            if (res2 !== '網頁PPT') return;

            if (!result[res1]) {
              result[res1] = {};
            }
            if (!result[res1][res2]) {
              result[res1][res2] = {};
            }
            if (!result[res1][res2][resources3]) {
              result[res1][res2][resources3] = [];
            }
            // 避免重複添加
            if (
              !result[res1][res2][resources3].find(
                r => r.resource._id === item._id,
              )
            ) {
              // 將 TInternetResource 轉換成 TDownloadableResource
              const downloadableResource: TDownloadableResource = {
                resource: {
                  _id: item._id,
                  categories: [],
                  versions: [],
                  years: item.years,
                  eduCodes: item.eduCodes,
                  subjects: item.subjects,
                  grades: item.grades,
                  originalFileId: item._id,
                  resources1: item.resources1,
                  resources2: item.resources2,
                  resources3: [resources3],
                  chapterKeyword: [],
                  knowledge: [],
                  fileName: item.urlName,
                  displayName: item.urlName,
                  isLaunch: item.isLaunch,
                  coverImg: '',
                  bookId: '',
                  file: {
                    _id: item._id,
                    category: convertInternetCategoryToResourceCategory(
                      item.category,
                    ),
                    version: 'v1' as resourceVersion,
                    year: item.years[0] || '',
                    eduCode: item.eduCodes[0] || ('elementary' as string),
                    subject: item.subjects[0] || '',
                    grade: item.grades[0] || '',
                    resource1: res1,
                    resource2: res2,
                    resource3: resources3,
                    fileName: item.urlName,
                    originalFileId: item._id,
                    fileUploadTime: '',
                    url: item.url, // 關鍵：將網路資源的 URL 放在這裡
                    fileSize: 0,
                    dataSource: 'internet',
                    bookId: '',
                  },
                },
                progress: emptyProgress,
              };

              result[res1][res2][resources3].push(downloadableResource);
            }
          });
        });
      });
    return result;
  }, [internetResources, resources1]);

  const [internetTab, setInternetTab] = useState('');
  const ebookPlatformTabs = useMemo(
    () =>
      resources1 === SIDEBAR.ebook
        ? (EBOOK_PLATFORM_TABS_BY_EDU_CODE[eduCodes ?? ''] ?? [])
        : [],
    [resources1, eduCodes],
  );
  const hasEbookPlatformTabs = ebookPlatformTabs.length > 0;
  const [ebookPlatform, setEbookPlatform] = useState<EbookPlatform>(() =>
    getCurrentEbookPlatform(ebookPlatformTabs, eduCodes ?? ''),
  );
  const hasManuallySelectedEbookPlatform = useRef(false);

  const handleEbookPlatformChange = (platform: EbookPlatform) => {
    hasManuallySelectedEbookPlatform.current = true;
    setEbookPlatform(platform);
  };

  useEffect(() => {
    if (!hasEbookPlatformTabs) return;
    setEbookPlatform(prev =>
      hasManuallySelectedEbookPlatform.current &&
      ebookPlatformTabs.includes(prev)
        ? prev
        : getCurrentEbookPlatform(ebookPlatformTabs, eduCodes ?? ''),
    );
  }, [
    ebookPlatformTabs,
    hasEbookPlatformTabs,
    eduCodes,
    years,
    subjects,
    grades,
  ]);

  useEffect(() => {
    setInternetTab(sortedTabs[0] || '');
  }, [sortedTabs]);

  const hasSearchCondition = useMemo(
    () => !!eduCodes && !!subjects,
    [eduCodes, years, grades, subjects],
  );

  const { resources, totalResourcesCount, totalPages } = useSelector(state =>
    getterClientResources(state, resourcePayload),
  );
  const pageSize =
    (resources1 as SIDEBAR) === SIDEBAR.latestNews
      ? 5
      : resources1 &&
          (resources1 as SIDEBAR) !== SIDEBAR.ebook &&
          (resources1 as SIDEBAR) !== SIDEBAR.tiku
        ? (resources1 as SIDEBAR) === SIDEBAR.internet
          ? 100
          : 4
        : 30;

  const multipleLevelResources = useSelector(state =>
    getterMultiLevelClientResources(state, resourcePayload),
  );

  const [accumulatedResource, setAccumulatedResource] = useState<
    TDownloadableResource[]
  >([]);
  const ebookResources = useMemo(() => {
    if (!hasEbookPlatformTabs) {
      return accumulatedResource.filter(
        item => getResourceFileExtension(item) === 'iso',
      );
    }

    if (ebookPlatform === EBOOK_PLATFORM.WEB) {
      return uniqBy(
        accumulatedResource.filter(
          item =>
            EBOOK_EXTENSIONS.includes(getResourceFileExtension(item)) &&
            !!item.resource.bookId,
        ),
        item => item.resource.bookId,
      ).map(item => ({
        ...item,
        resource: {
          ...item.resource,
          displayName: stripEbookFileExtension(item.resource.displayName),
        },
      }));
    }

    const extensions =
      ebookPlatform === EBOOK_PLATFORM.WINDOWS
        ? EBOOK_WINDOWS_EXTENSIONS
        : EBOOK_MACOS_EXTENSIONS;
    return accumulatedResource.filter(item =>
      extensions.includes(getResourceFileExtension(item)),
    );
  }, [accumulatedResource, ebookPlatform, hasEbookPlatformTabs]);
  const [
    accumulatedMultipleLevelResources,
    setAccumulatedMultipleLevelResources,
  ] = useState<{
    [key: string]: {
      [key: string]: { [key: string]: TDownloadableResource[] };
    };
  }>({ '': {} });

  const progressLookup = useSelector(getterProgressLookup);
  // 累積清單裡前面頁面的下載狀態，停在那一頁還是「目前頁」時的快照，之後不會再更新，
  // 畫面要用的狀態一律依 url 即時查（NBX3-1117）
  const liveMultipleLevelResources = useMemo(
    () =>
      mapValues(accumulatedMultipleLevelResources, resource2Content =>
        mapValues(resource2Content, resource3Content =>
          mapValues(resource3Content, items =>
            withLiveProgress(items, progressLookup),
          ),
        ),
      ),
    [accumulatedMultipleLevelResources, progressLookup],
  );

  const mergedResources = useMemo(() => {
    if (resources1 !== SIDEBAR.ppt) {
      return liveMultipleLevelResources;
    }

    // 合併網路資源和原本的教學資源
    const merged: typeof accumulatedMultipleLevelResources = {};

    // 先處理網路資源（讓它們在前面）
    Object.entries(internetResourcesGrouped).forEach(
      ([resource1, resource1Content]) => {
        if (!merged[resource1]) {
          merged[resource1] = {};
        }

        Object.entries(resource1Content).forEach(
          ([resource2, resource2Content]) => {
            if (!merged[resource1][resource2]) {
              merged[resource1][resource2] = {};
            }

            Object.entries(resource2Content).forEach(
              ([resource3, resource3Items]) => {
                // 先添加網路資源
                merged[resource1][resource2][resource3] = [...resource3Items];
              },
            );
          },
        );
      },
    );

    // 然後再合併原本的教學資源（添加到後面）
    Object.entries(liveMultipleLevelResources).forEach(
      ([resource1, resource1Content]) => {
        if (!merged[resource1]) {
          merged[resource1] = {};
        }

        Object.entries(resource1Content).forEach(
          ([resource2, resource2Content]) => {
            if (!merged[resource1][resource2]) {
              merged[resource1][resource2] = {};
            }

            Object.entries(resource2Content).forEach(
              ([resource3, resource3Items]) => {
                if (!merged[resource1][resource2][resource3]) {
                  // 如果該 resource3 不存在，直接添加
                  merged[resource1][resource2][resource3] = [...resource3Items];
                } else {
                  // 如果該 resource3 已存在（來自網路資源），將原本的教學資源添加到後面
                  resource3Items.forEach(item => {
                    if (
                      !merged[resource1][resource2][resource3].find(
                        r => r.resource._id === item.resource._id,
                      )
                    ) {
                      merged[resource1][resource2][resource3].push(item);
                    }
                  });
                }
              },
            );
          },
        );
      },
    );

    return merged;
  }, [resources1, liveMultipleLevelResources, internetResourcesGrouped]);

  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState<boolean>(false);
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const infiniteRef = useInfiniteScroll({
    loading,
    hasNextPage,
    onLoadMore: () => {
      if (resources1 === SIDEBAR.internet) return;
      setLoading(true);
      changePage(storePage + 1);
    },
  });

  const changePage = (count: number) => {
    dispatch(
      setSearchCondition({
        pageSize,
        page: count,
      }),
    );
    // 網路資源不跟著一般資源翻頁：教學PPT 頁的網頁PPT 是一次抓完後疊在畫面上，
    // 若跟著換頁會抓到第 N 頁（多半為空）而讓已顯示的網頁PPT 消失。
    dispatch(
      setInterSearchCondition({
        pageSize: resolveInternetPageSize(resources1),
        page: 0,
      }),
    );
  };

  const checkEbookAndTikuType = (type: string) => {
    switch (type) {
      case SIDEBAR.ebook:
      case SIDEBAR.tiku:
        return true;
      default:
        return false;
    }
  };

  useEffect(() => {
    const payload = {
      pageSize,
      page: storePage,
      eduCodes: eduCodes ?? '',
      years: years ?? '',
      grades: grades ?? '',
      subjects: subjects ?? '',
      resources1: resources1 ?? '',
    };
    dispatch(setSearchCondition(payload));
    // 網路資源不能沿用側欄的 pageSize，否則初次載入就會被蓋成 4
    dispatch(
      setInterSearchCondition({
        ...payload,
        pageSize: resolveInternetPageSize(resources1),
      }),
    );
  }, []);

  useEffect(() => {
    // 若為網路資源，hasNextPage 設為 false
    setHasNextPage(!(resources1 === SIDEBAR.internet));

    // 避免初次抓取資源時， api 有取得資料卻沒顯示而註解。未來可能需要再開啟。
    // if (storePage === 0) {
    //   setAccumulatedResource([]);
    //   setAccumulatedMultipleLevelResources({ '': {} });
    //   return;
    // }

    let accumulatedCount = 0;
    let incomingCount = 0;

    // 處理題庫及 Ebook 資源
    if (checkEbookAndTikuType(resources1 || '')) {
      const newAccumulatedResource =
        storePage === 0
          ? resources
          : uniqBy(
              [...accumulatedResource, ...resources],
              el => el.resource._id,
            );
      accumulatedCount = storePage === 0 ? 0 : accumulatedResource.length;
      incomingCount =
        newAccumulatedResource.length === accumulatedCount
          ? 0
          : resources.length;

      setAccumulatedResource(newAccumulatedResource);
    }

    // 處理其他資源
    if (!checkEbookAndTikuType(resources1 || '')) {
      const key = Object.keys(multipleLevelResources)[0];
      const value = Object.values(multipleLevelResources);

      if (!key) {
        if (storePage === 0) {
          setAccumulatedMultipleLevelResources({ '': {} });
        }
      } else {
        setAccumulatedMultipleLevelResources(prev => {
          if (storePage === 0) {
            return multipleLevelResources;
          }
          const prevValue = prev?.[key];
          const newValue = [prevValue, value[0]];
          return {
            [key]: {
              ...newValue[0],
              ...newValue[1],
            },
          };
        });
      }
    }
    setLoading(false);
    // 依 resources2 分組分頁時，前端群組計數與 API totalAmount 的口徑不一致
    //（resources2=[] 的群組會 fallback 成 resources1 key 而重複計數），
    // 改依 API 回傳的 totalPages 判斷是否還有下一頁（page 為 0-indexed）
    setHasNextPage(
      checkEbookAndTikuType(resources1 || '')
        ? totalResourcesCount > incomingCount + accumulatedCount
        : storePage + 1 < totalPages,
    );
  }, [storePage, resources, multipleLevelResources, resources1]);

  useEffect(() => {
    setLoading(false); // 每次內容切換時重置 loading 狀態
  }, [resources1]);

  useEffect(() => {
    return () => {
      changePage(0);
    };
  }, []);

  if (!hasSearchCondition) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          minHeight: 'calc(100vh - 150px)',
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <StateCard
            imgSrc={NoSearchConditionImg}
            title="請先選擇學制與科目"
            content="以顯示屬於該科目的資源"
          />
        </Box>
        <SiteFooter />
      </Box>
    );
  }

  if (resources1 === SIDEBAR.latestNews) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          minHeight: 'calc(100vh - 150px)',
        }}
      >
        <Box sx={{ flex: 1, width: '100%' }}>
          <ResourceLatestNews />
        </Box>
        <SiteFooter />
      </Box>
    );
  }

  if (
    resources1 !== SIDEBAR.internet &&
    resources1 !== SIDEBAR.ebook &&
    resources.length === 0 &&
    !loading &&
    !isResourcesFetching
  ) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          minHeight: 'calc(100vh - 150px)',
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <StateCard imgSrc={noFileImg} title="資料建置中" content="" />
        </Box>
        <SiteFooter />
      </Box>
    );
  }

  if (
    resources1 === SIDEBAR.internet &&
    internetResources.resources.length === 0 &&
    !loading &&
    !isInternetResourcesLoading
  ) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          minHeight: 'calc(100vh - 150px)',
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <StateCard imgSrc={noFileImg} title="資料建置中" content="" />
        </Box>
        <SiteFooter />
      </Box>
    );
  }

  return (
    <resource.container
      sx={{
        ...(loading && {
          minHeight: '90vh',
        }),
      }}
    >
      <>
        {hasEbookPlatformTabs && (
          <Box sx={{ width: '100%' }}>
            <FilterTab
              tabs={ebookPlatformTabs}
              currentTab={ebookPlatform}
              setCurrentTab={handleEbookPlatformChange}
            />
          </Box>
        )}
        {resources1 === SIDEBAR.tiku && accumulatedResource.length > 0 && (
          <bookCard.Container>
            <bookCard.Head
              title={'新版線上雲端題庫'}
              titleTypographyProps={{ title: '新版線上雲端題庫' }}
            />
            <bookCard.Content>
              <CardMedia
                component="img"
                src={tikuCloud}
                sx={{ height: '238px', aspectRatio: '209/238' }}
              />
            </bookCard.Content>
            <bookCard.Action>
              <BaseButton
                text="前往"
                color={BASE_BUTTON_COLOR.PRIMARY}
                variant={BASE_BUTTON_VARIANT.GHOST}
                width="100%"
                style={{ fontSize: '18px' }}
                onClick={() => {
                  openExternal('https://onepaper.oneclass.com.tw/');
                }}
              />
            </bookCard.Action>
          </bookCard.Container>
        )}
        {resources1 === SIDEBAR.internet && (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
            }}
          >
            <FilterTab
              tabs={sortedTabs}
              currentTab={internetTab}
              setCurrentTab={setInternetTab}
              onTabChange={tab => {
                resourceInternetTabEvent({
                  edu: eduCodes ?? undefined,
                  subject: subjects ?? undefined,
                  year: years ?? undefined,
                  grade: grades ?? undefined,
                  tab_name: tab,
                });
              }}
            />
            <internet.container>
              {internetResources.resources
                .filter(res =>
                  res.resources2.find(res2 => res2 === internetTab),
                )
                .map(item => (
                  <InternatCard
                    title={item.urlName}
                    key={item._id}
                    url={item.url}
                    onLinkClick={() => {
                      resourceInternetLinkClickEvent({
                        edu: eduCodes ?? undefined,
                        subject: subjects ?? undefined,
                        year: years ?? undefined,
                        grade: grades ?? undefined,
                        tab_name: internetTab,
                        link_name: item.urlName,
                        link: item.url,
                      });
                    }}
                  />
                ))}
            </internet.container>
          </Box>
        )}
        {checkEbookAndTikuType(resources1 || '') &&
          (resources1 === SIDEBAR.ebook
            ? ebookResources
            : accumulatedResource
          ).map(item => {
            // 網頁版借用去重後留下的那筆 resource，但它的下載進度與網頁版無關
            const isWebReaderCard =
              hasEbookPlatformTabs && ebookPlatform === EBOOK_PLATFORM.WEB;
            return (
              <BookCard
                key={item.resource._id}
                overview={item}
                progress={
                  isWebReaderCard
                    ? emptyProgress
                    : progressLookup(item.resource.file.url)
                }
                webReaderBookId={
                  isWebReaderCard ? item.resource.bookId : undefined
                }
                isMacOnly={
                  hasEbookPlatformTabs && ebookPlatform === EBOOK_PLATFORM.MACOS
                }
              />
            );
          })}
        {resources1 === SIDEBAR.ebook &&
          !loading &&
          !hasNextPage &&
          !isResourcesFetching &&
          ebookResources.length === 0 && (
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'center',
                minHeight: '384px',
                width: '100%',
              }}
            >
              <StateCard imgSrc={noFileImg} title="資料建置中" content="" />
            </Box>
          )}
        {!checkEbookAndTikuType(resources1 || '') &&
          Object.entries(mergedResources).map(([label, content]) => {
            return (
              <ListDownloadGroup
                lists={content}
                key={label}
                resource1={label}
              />
            );
          })}
      </>

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
        ref={infiniteRef}
      >
        {resources1 === SIDEBAR.internet ? (
          <Pagination
            totalResourcesCount={Math.ceil(totalResourcesCount / pageSize)}
            pageSize={pageSize}
            page={storePage}
            onChange={changePage}
          />
        ) : (
          (loading || hasNextPage) && (
            <resource.stackContainer>
              <Stack justifyContent="center" alignItems="center">
                <CircularProgress sx={{ color: '00A15D' }} />
                <div>載入中...</div>
              </Stack>
            </resource.stackContainer>
          )
        )}
      </Box>
      <ToTopButton />
      <IssueButton />
      <SiteFooter />
    </resource.container>
  );
};
